import { Cell, Grid } from 'baseui/layout-grid'
import GenericInput from './Filters/GenericInput'
import GenericButtonGroup from './Filters/GenericButtonGroup'
import GenericSelect from './Filters/GenericSelect'
import ByCreationDate from './Filters/ByCreationDate'
import useContractsFilters from './useContractsFilters'
import { DashboardV2Filters } from '../types'

interface ContractsFiltersProps {
  filters: DashboardV2Filters
  providersList: Record<string, unknown>
  usersList: Record<string, unknown>
  applicationType: string
}

const ContractsFilters = ({ filters, providersList, usersList, applicationType }: ContractsFiltersProps) => {
  const { t, css, theme, dispatchFilters, manageCheckboxGroup, manageRadioGroup, mapValues } = useContractsFilters({
    filters,
  })

  const { status, energy, productType, prestation, productTypeInsurance, prestationInsurance, prestationEnergy, contractType, details, providerId, userId } = filters

  const getProviderLabel = (appType: string) => {
    if (appType === 'energie') return t('dashboard:filters.by_provider_energie')
    if (appType === 'telecom') return t('dashboard:filters.by_provider_telecom')
    return t('dashboard:filters.by_provider_insurance')
  }

  return (
    <div
      className={css({
        marginBottom: '2rem',
        backgroundColor: 'white',
        padding: '1rem',
        borderRadius: '1rem',
        boxShadow: '0 2px 6px rgba(20,96,170,.3)',
      })}
    >
      <Grid>
        <Cell span={12}>
          <h4 className={css({ fontSize: '1rem', color: theme.colors.primary })}>
            {t('dashboard:filters.title')}
          </h4>
        </Cell>
        <Cell span={12}>
          <Grid>
            <Cell span={4}>
              <GenericButtonGroup
                values={t('dashboard:filters.status_options', { returnObjects: true }) as string[]}
                selected={mapValues(status, 'status', true) as number[] | undefined}
                onClick={(event, index) => manageRadioGroup(event, index, 'status')}
                label={t('dashboard:filters.status')}
              />
            </Cell>

            {applicationType === 'energie' && (
              <Cell span={3}>
                <GenericButtonGroup
                  values={t('dashboard:filters.energy_options', { returnObjects: true }) as string[]}
                  selected={mapValues(energy, 'energy', true) as number[] | undefined}
                  onClick={(event, index) => manageRadioGroup(event, index, 'energy')}
                  label={t('dashboard:filters.by_energy')}
                />
              </Cell>
            )}

            {applicationType === 'telecom' && (
              <>
                <Cell span={3}>
                  <GenericButtonGroup
                    values={t('dashboard:filters.product_type_telecom_options', { returnObjects: true }) as string[]}
                    selected={mapValues(productType, 'productType', true) as number[] | undefined}
                    onClick={(event, index) => manageRadioGroup(event, index, 'productType')}
                    label={t('dashboard:filters.by_product_type')}
                  />
                </Cell>
                <Cell span={2}>
                  <GenericButtonGroup
                    values={t('dashboard:filters.prestation_telecom_options', { returnObjects: true }) as string[]}
                    selected={mapValues(prestation, 'prestation', true) as number[] | undefined}
                    onClick={(event, index) => manageRadioGroup(event, index, 'prestation')}
                    label={t('dashboard:filters.prestation_type')}
                  />
                </Cell>
              </>
            )}

            {applicationType === 'assurance' && (
              <>
                <Cell span={3}>
                  <GenericButtonGroup
                    values={t('dashboard:filters.product_type_insurance_options', { returnObjects: true }) as string[]}
                    selected={mapValues(productTypeInsurance, 'productTypeInsurance', true) as number[] | undefined}
                    onClick={(event, index) => manageRadioGroup(event, index, 'productTypeInsurance')}
                    label={t('dashboard:filters.by_product_type')}
                  />
                </Cell>
                <Cell span={2}>
                  <GenericButtonGroup
                    values={t('dashboard:filters.prestation_insurance_options', { returnObjects: true }) as string[]}
                    selected={mapValues(prestationInsurance, 'prestationInsurance', true) as number[] | undefined}
                    onClick={(event, index) => manageRadioGroup(event, index, 'prestationInsurance')}
                    label={t('dashboard:filters.prestation_type')}
                  />
                </Cell>
              </>
            )}

            {applicationType === 'energie' && (
              <>
                <Cell span={(t('dashboard:filters.prestation_energy_options', { returnObjects: true }) as string[]).length > 3 ? 4 : 2}>
                  <GenericButtonGroup
                    values={t('dashboard:filters.prestation_energy_options', { returnObjects: true }) as string[]}
                    selected={mapValues(prestationEnergy, 'prestationEnergy', true) as number[] | undefined}
                    onClick={(event, index) => manageRadioGroup(event, index, 'prestationEnergy')}
                    label={t('dashboard:filters.prestation_type')}
                  />
                </Cell>
                <Cell span={2}>
                  <GenericButtonGroup
                    values={t('dashboard:filters.contract_type_options', { returnObjects: true }) as string[]}
                    selected={mapValues(contractType, 'contractType', true) as number[] | undefined}
                    onClick={(event, index) => manageRadioGroup(event, index, 'contractType')}
                    label={t('dashboard:filters.contract_type')}
                  />
                </Cell>
              </>
            )}

            <Cell span={2}>
              <GenericInput
                label={t('dashboard:filters.contract_id')}
                onBlur={event => dispatchFilters({ ...filters, subscriptionId: event.target.value })}
              />
            </Cell>
            <Cell span={2}>
              <GenericSelect
                label={getProviderLabel(applicationType)}
                onChange={params => dispatchFilters({ ...filters, providerId: String(params.value[0]?.id ?? '') })}
                list={providersList}
                getId={(provider: unknown) => (provider as { id: string }).id}
                getName={(provider: unknown) => (provider as { name: string }).name}
                filterId={providerId}
              />
            </Cell>
            <Cell span={2}>
              <GenericInput
                label={t('dashboard:filters.lastname')}
                onBlur={event => dispatchFilters({ ...filters, lastname: event.target.value })}
              />
            </Cell>
            <Cell span={2}>
              <GenericInput
                label={t('dashboard:filters.firstname')}
                onBlur={event => dispatchFilters({ ...filters, firstname: event.target.value })}
              />
            </Cell>
            <Cell span={3}>
              <GenericInput
                label={t('dashboard:filters.email')}
                onBlur={event => dispatchFilters({ ...filters, email: event.target.value })}
              />
            </Cell>
            <Cell span={2}>
              <GenericInput
                label={t('dashboard:filters.phone')}
                onBlur={event => dispatchFilters({ ...filters, phone: event.target.value })}
              />
            </Cell>
            <Cell span={3}>
              <GenericInput
                label={t('dashboard:filters.zoho_id')}
                onBlur={event => dispatchFilters({ ...filters, zohoId: event.target.value })}
              />
            </Cell>
            <Cell span={2}>
              <ByCreationDate
                label={t('dashboard:filters.creation_date_from')}
                onDateChangeHandler={date => dispatchFilters({ ...filters, creationFrom: date })}
              />
            </Cell>
            <Cell span={2}>
              <ByCreationDate
                label={t('dashboard:filters.creation_date_to')}
                onDateChangeHandler={date => dispatchFilters({ ...filters, creationTo: date })}
              />
            </Cell>

            {applicationType === 'energie' && (
              <>
                <Cell span={3}>
                  <GenericInput
                    label={t('dashboard:filters.meter_id_elec')}
                    onBlur={event => dispatchFilters({ ...filters, meterIdElectricity: event.target.value })}
                  />
                </Cell>
                <Cell span={3}>
                  <GenericInput
                    label={t('dashboard:filters.meter_id_gas')}
                    onBlur={event => dispatchFilters({ ...filters, meterIdGas: event.target.value })}
                  />
                </Cell>
              </>
            )}

            <Cell span={2}>
              <GenericSelect
                label={t('dashboard:filters.by_user')}
                onChange={params => dispatchFilters({ ...filters, userId: String(params.value[0]?.id ?? '') })}
                list={usersList}
                getId={(user: unknown) => (user as { id: string }).id}
                getName={(user: unknown) => {
                  const u = user as { firstname: string; name: string }
                  return u.firstname + ' ' + u.name
                }}
                filterId={userId}
              />
            </Cell>
            <Cell span={3}>
              <GenericButtonGroup
                values={[t('dashboard:filters.my_subscriptions')]}
                selected={mapValues(details, 'details', true) as number[] | undefined}
                onClick={(event, index) => manageCheckboxGroup(event, index, 'details')}
                label={t('dashboard:filters.by_details')}
              />
            </Cell>
          </Grid>
        </Cell>
      </Grid>
    </div>
  )
}

export default ContractsFilters
